import React, { useEffect, useState, useMemo, useCallback } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Collapse,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormLabel,
  InputLabel,
  MenuItem,
  Paper,
  Radio,
  RadioGroup,
  Select,
  Snackbar,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import AppHeader from '../AppHeader';
import { useAuth } from '../../contexts/AuthContext';
import { sessionAPI, studentAPI } from '../../services/api';
import {
  COMMUNICATION_CATEGORIES,
  createCampaign,
  getCampaign,
  getStaffPositionGroups,
  patchCampaign,
  previewCampaign,
  searchEmployees,
  sendCampaign,
} from '../../services/communications';

const AUDIENCE = [
  { value: 'all_parents', label: 'All Parents' },
  { value: 'by_session', label: 'By Session' },
  { value: 'by_classroom', label: 'By Classroom' },
  { value: 'specific_students', label: 'Specific Students' },
  { value: 'staff', label: 'Staff — All' },
  { value: 'staff_by_position', label: 'Staff — By Position Group' },
  { value: 'staff_specific', label: 'Staff — Specific Staff' },
];

function smsSegments(len) {
  if (len <= 0) return 0;
  return Math.ceil(len / 160);
}

export default function CampaignComposer() {
  const navigate = useNavigate();
  const { id: editCampaignId } = useParams();
  const { user } = useAuth();
  const readOnly = user?.role === 'viewer';
  const canEmergencyPriority = user?.role === 'superadmin' || user?.role === 'admin';

  const [title, setTitle] = useState('');
  const [category, setCategory] = useState('general');
  const [priority, setPriority] = useState('normal');
  const [bodyPlain, setBodyPlain] = useState('');
  const [audienceType, setAudienceType] = useState('all_parents');
  const [sessionId, setSessionId] = useState('');
  const [classNum, setClassNum] = useState('');
  const [sessions, setSessions] = useState([]);
  const [classrooms, setClassrooms] = useState([]);
  const [studentOptions, setStudentOptions] = useState([]);
  const [selectedStudents, setSelectedStudents] = useState([]);
  const [studentSearchSession, setStudentSearchSession] = useState('');

  const [positionGroupKey, setPositionGroupKey] = useState('');
  const [staffPositionGroups, setStaffPositionGroups] = useState([]);
  const [selectedStaff, setSelectedStaff] = useState([]);
  const [staffSearchSession, setStaffSearchSession] = useState('');
  const [staffSearchInput, setStaffSearchInput] = useState('');
  const [staffSearchOptions, setStaffSearchOptions] = useState([]);

  const [campaignId, setCampaignId] = useState(null);
  const [initialLoad, setInitialLoad] = useState(Boolean(editCampaignId));
  const [previewDetail, setPreviewDetail] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [sending, setSending] = useState(false);
  const [savingDraft, setSavingDraft] = useState(false);
  const [error, setError] = useState(null);
  const [confirmSendOpen, setConfirmSendOpen] = useState(false);
  const [toast, setToast] = useState('');

  const bodyLen = bodyPlain.length;
  const segments = smsSegments(bodyLen);

  const isEditMode = Boolean(editCampaignId);

  const clearAudienceFields = useCallback((nextType) => {
    if (
      nextType !== 'by_session' &&
      nextType !== 'by_classroom' &&
      nextType !== 'staff'
    ) {
      setSessionId('');
      setClassNum('');
    }
    if (nextType !== 'by_classroom') {
      setClassNum('');
    }
    if (nextType !== 'specific_students') {
      setSelectedStudents([]);
      setStudentSearchSession('');
    }
  }, []);

  const onAudienceChange = (next) => {
    if (!['staff', 'staff_by_position', 'staff_specific'].includes(next)) {
      setPositionGroupKey('');
      setSelectedStaff([]);
      setStaffSearchSession('');
      setStaffSearchInput('');
      setStaffSearchOptions([]);
    }
    if (next === 'staff_by_position' || next === 'staff_specific') {
      setSessionId('');
    }
    if (next !== 'staff_by_position') setPositionGroupKey('');
    if (next !== 'staff_specific') {
      setSelectedStaff([]);
      setStaffSearchSession('');
      setStaffSearchInput('');
      setStaffSearchOptions([]);
    }
    setAudienceType(next);
    clearAudienceFields(next);
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const sess = await sessionAPI.getSessions();
        if (cancelled) return;
        const sl = sess.data?.results || sess.data || [];
        setSessions(Array.isArray(sl) ? sl : []);
      } catch (e) {
        if (!cancelled) console.error(e);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const groups = await getStaffPositionGroups();
        if (!cancelled) setStaffPositionGroups(Array.isArray(groups) ? groups : []);
      } catch (e) {
        if (!cancelled) console.error(e);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!isEditMode || !editCampaignId) return;
    let cancelled = false;
    (async () => {
      setInitialLoad(true);
      setError(null);
      try {
        const c = await getCampaign(editCampaignId);
        if (cancelled) return;
        if (c.status !== 'draft' && c.status !== 'previewed') {
          navigate(`/communications/${editCampaignId}`, { replace: true });
          return;
        }
        setCampaignId(c.id);
        setTitle(c.title || '');
        setCategory(c.category || 'general');
        setPriority(c.priority || 'normal');
        setBodyPlain(c.body_plain || '');
        const at = c.audience_type || 'all_parents';
        setAudienceType(at);
        setPositionGroupKey(at === 'staff_by_position' ? (c.position_group || '').trim() : '');
        setSessionId(c.session ? String(c.session) : '');
        setClassNum(c.classroom_class_num || '');
        const ids = c.specific_student_ids || [];
        if (Array.isArray(ids) && ids.length) {
          const sid = c.session ? String(c.session) : '';
          setStudentSearchSession(sid);
          if (sid) {
            const res = await studentAPI.getStudents(Number(sid), { page_size: 500 });
            if (cancelled) return;
            const list = res.data?.results || res.data || [];
            const picked = (Array.isArray(list) ? list : []).filter((s) => ids.includes(s.id));
            setSelectedStudents(picked);
          }
        }
        setSelectedStaff([]);
        if (at === 'staff_specific' && Array.isArray(c.staff_ids) && c.staff_ids.length) {
          try {
            const hid = await searchEmployees({ ids: c.staff_ids.slice(0, 20).join(',') });
            if (!cancelled) setSelectedStaff(Array.isArray(hid) ? hid : []);
          } catch {
            if (!cancelled) setSelectedStaff([]);
          }
        }
      } catch (e) {
        if (!cancelled) setError(e.response?.data?.detail || e.message || 'Failed to load campaign');
      } finally {
        if (!cancelled) setInitialLoad(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [editCampaignId, isEditMode, navigate]);

  useEffect(() => {
    if (audienceType !== 'by_classroom') {
      setClassrooms([]);
      return;
    }
    if (!sessionId) {
      setClassrooms([]);
      return;
    }
    let cancelled = false;
    sessionAPI.getClassrooms(Number(sessionId)).then((res) => {
      if (cancelled) return;
      const list = res.data?.results || res.data || [];
      setClassrooms(Array.isArray(list) ? list : []);
    });
    return () => {
      cancelled = true;
    };
  }, [sessionId, audienceType]);

  useEffect(() => {
    if (audienceType !== 'specific_students' || !studentSearchSession) {
      setStudentOptions([]);
      return;
    }
    let cancelled = false;
    studentAPI.getStudents(Number(studentSearchSession), { page_size: 500 }).then((res) => {
      if (cancelled) return;
      const list = res.data?.results || res.data || [];
      setStudentOptions(Array.isArray(list) ? list : []);
    });
    return () => {
      cancelled = true;
    };
  }, [studentSearchSession, audienceType]);

  useEffect(() => {
    if (audienceType !== 'staff_specific') return undefined;
    const q = staffSearchInput.trim();
    if (q.length < 1) {
      setStaffSearchOptions([]);
      return undefined;
    }
    let cancelled = false;
    const t = setTimeout(async () => {
      try {
        const rows = await searchEmployees({
          q,
          session_id: staffSearchSession ? Number(staffSearchSession) : undefined,
        });
        if (!cancelled) setStaffSearchOptions(Array.isArray(rows) ? rows : []);
      } catch (e) {
        if (!cancelled) console.error(e);
      }
    }, 300);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [staffSearchInput, staffSearchSession, audienceType]);

  const payloadBase = useMemo(() => {
    const sessionPayload = ['by_session', 'by_classroom'].includes(audienceType)
      ? sessionId
        ? Number(sessionId)
        : null
      : audienceType === 'staff'
        ? sessionId
          ? Number(sessionId)
          : null
        : audienceType === 'specific_students'
          ? studentSearchSession
            ? Number(studentSearchSession)
            : null
          : null;

    return {
      title: title.trim(),
      category,
      priority,
      body_plain: bodyPlain,
      audience_type: audienceType,
      session: sessionPayload,
      classroom_class_num: audienceType === 'by_classroom' && classNum ? classNum : null,
      specific_student_ids:
        audienceType === 'specific_students' ? selectedStudents.map((s) => s.id) : [],
      position_group: audienceType === 'staff_by_position' ? positionGroupKey : '',
      staff_ids: audienceType === 'staff_specific' ? selectedStaff.map((s) => s.id) : [],
    };
  }, [
    title,
    category,
    priority,
    bodyPlain,
    audienceType,
    sessionId,
    classNum,
    selectedStudents,
    studentSearchSession,
    positionGroupKey,
    selectedStaff,
  ]);

  const persistDraft = async () => {
    if (readOnly) return null;
    let id = campaignId;
    if (!id) {
      const created = await createCampaign(payloadBase);
      id = created.id;
      setCampaignId(id);
      if (!isEditMode) {
        navigate(`/communications/${id}/edit`, { replace: true });
      }
    } else {
      await patchCampaign(id, payloadBase);
    }
    return id;
  };

  const handleSaveDraft = async () => {
    if (readOnly) return;
    setSavingDraft(true);
    setError(null);
    try {
      await persistDraft();
      setToast('Draft saved');
    } catch (e) {
      setError(e.response?.data?.detail || e.message || 'Save failed');
    } finally {
      setSavingDraft(false);
    }
  };

  const handlePreview = async () => {
    if (readOnly) return;
    setError(null);
    setPreviewing(true);
    setPreviewDetail(null);
    try {
      const id = await persistDraft();
      if (!id) return;
      const prev = await previewCampaign(id);
      setPreviewDetail(prev);
    } catch (e) {
      setError(e.response?.data?.detail || e.message || 'Preview failed');
    } finally {
      setPreviewing(false);
    }
  };

  const handleSend = async () => {
    if (!campaignId || readOnly) return;
    setSending(true);
    setError(null);
    try {
      await sendCampaign(campaignId);
      setConfirmSendOpen(false);
      setToast('Send queued');
      navigate(`/communications/${campaignId}`);
    } catch (e) {
      setError(e.response?.data?.detail || e.message || 'Send failed');
    } finally {
      setSending(false);
    }
  };

  const sendConfirmText =
    previewDetail != null
      ? previewDetail.audience_kind === 'staff'
        ? `Send to ${previewDetail.recipient_count} staff members via SMS?`
        : `Send to ${previewDetail.recipient_count} recipients via SMS and/or voice (TTS)?`
      : 'Send this campaign to all matched recipients via SMS and/or voice (TTS)?';

  if (initialLoad) {
    return (
      <Box>
        <AppHeader backTo="/communications" />
        <Container sx={{ mt: 3 }}>
          <Typography>Loading campaign…</Typography>
        </Container>
      </Box>
    );
  }

  return (
    <Box>
      <AppHeader backTo="/communications" />
      <Container maxWidth="md" sx={{ mt: 3, mb: 4 }}>
        <Typography variant="h5" sx={{ fontWeight: 600, mb: 1 }}>
          {isEditMode ? 'Edit campaign' : 'New campaign'}
        </Typography>
        {readOnly && (
          <Alert severity="info" sx={{ mb: 2 }}>
            Viewers have read-only access.
          </Alert>
        )}
        {priority === 'emergency' && !canEmergencyPriority && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            Emergency messages are restricted to administrators. The server will reject send if you are not allowed.
          </Alert>
        )}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        <TextField
          label="Title"
          fullWidth
          margin="normal"
          required
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          disabled={readOnly}
          helperText="Internal reference — parents do not see this."
        />

        <FormControl fullWidth margin="normal" size="small">
          <InputLabel id="cat-label">Category</InputLabel>
          <Select
            labelId="cat-label"
            label="Category"
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            disabled={readOnly}
          >
            {COMMUNICATION_CATEGORIES.map((c) => (
              <MenuItem key={c.value} value={c.value}>
                {c.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <Box sx={{ my: 2 }}>
          <FormLabel component="legend">Priority</FormLabel>
          <ToggleButtonGroup
            exclusive
            value={priority}
            onChange={(_, v) => v && setPriority(v)}
            size="small"
            sx={{ mt: 1 }}
            disabled={readOnly}
          >
            <ToggleButton value="normal">Normal</ToggleButton>
            <ToggleButton value="emergency" disabled={!canEmergencyPriority}>
              Emergency
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>

        <TextField
          label="Message body"
          fullWidth
          multiline
          minRows={8}
          margin="normal"
          value={bodyPlain}
          onChange={(e) => setBodyPlain(e.target.value)}
          disabled={readOnly}
          helperText="SMS text and voice (TTS) script."
        />
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
          {bodyLen} characters ({segments} SMS segment{segments === 1 ? '' : 's'})
        </Typography>

        <FormControl component="fieldset" sx={{ mt: 2, width: '100%' }}>
          <FormLabel component="legend">Audience</FormLabel>
          <RadioGroup value={audienceType} onChange={(e) => onAudienceChange(e.target.value)} sx={{ mt: 1 }}>
            {AUDIENCE.map((a) => (
              <FormControlLabel
                key={a.value}
                value={a.value}
                control={<Radio disabled={readOnly} />}
                label={a.label}
              />
            ))}
          </RadioGroup>
        </FormControl>

        {(audienceType === 'by_session' ||
          audienceType === 'by_classroom' ||
          audienceType === 'staff') && (
          <Autocomplete
            options={sessions}
            getOptionLabel={(o) => o.name || `Session ${o.id}`}
            value={sessions.find((s) => String(s.id) === sessionId) || null}
            onChange={(_, v) => {
              setSessionId(v ? String(v.id) : '');
              setClassNum('');
            }}
            disabled={readOnly}
            renderInput={(params) => (
              <TextField
                {...params}
                label={audienceType === 'staff' ? 'Session (optional — all active if empty)' : 'Session'}
                margin="normal"
              />
            )}
          />
        )}

        {audienceType === 'staff_by_position' && (
          <FormControl fullWidth margin="normal" size="small">
            <InputLabel id="staff-pos-label">Position group</InputLabel>
            <Select
              labelId="staff-pos-label"
              label="Position group"
              value={positionGroupKey}
              onChange={(e) => setPositionGroupKey(e.target.value)}
              disabled={readOnly}
            >
              <MenuItem value="">
                <em>Select position group…</em>
              </MenuItem>
              {staffPositionGroups.map((row) => (
                <MenuItem key={row.key} value={row.key}>
                  {row.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}

        {audienceType === 'staff_specific' && (
          <>
            <Autocomplete
              options={sessions}
              getOptionLabel={(o) => o.name || `Session ${o.id}`}
              value={sessions.find((s) => String(s.id) === staffSearchSession) || null}
              onChange={(_, v) => {
                setStaffSearchSession(v ? String(v.id) : '');
                setStaffSearchOptions([]);
              }}
              disabled={readOnly}
              renderInput={(params) => (
                <TextField {...params} label="Session (optional — filter search)" margin="normal" />
              )}
            />
            <Autocomplete
              multiple
              filterOptions={(opts) => opts}
              options={staffSearchOptions}
              getOptionLabel={(o) =>
                `${o.first_name || ''} ${o.last_name || ''}`.trim() || `#${o.id}`
              }
              isOptionEqualToValue={(a, b) => a.id === b.id}
              value={selectedStaff}
              onChange={(_, v) => {
                setSelectedStaff(v);
                setStaffSearchInput('');
              }}
              inputValue={staffSearchInput}
              onInputChange={(_, v) => setStaffSearchInput(v)}
              disabled={readOnly}
              renderOption={(props, option) => (
                <li {...props} key={option.id}>
                  {`${option.first_name || ''} ${option.last_name || ''}`.trim() || `#${option.id}`}
                  {!option.phone ? ' (no phone)' : ''}
                  {option.position ? ` — ${option.position}` : ''}
                </li>
              )}
              renderInput={(params) => (
                <TextField {...params} label="Search staff by name" margin="normal" placeholder="Type to search…" />
              )}
            />
          </>
        )}

        {audienceType === 'by_classroom' && (
          <Autocomplete
            options={classrooms}
            getOptionLabel={(c) => `${c.class_num}${c.class_size != null ? ` (${c.class_size})` : ''}`}
            value={classrooms.find((c) => String(c.class_num) === String(classNum)) || null}
            onChange={(_, v) => setClassNum(v ? String(v.class_num) : '')}
            disabled={readOnly || !sessionId}
            renderInput={(params) => <TextField {...params} label="Classroom" margin="normal" />}
          />
        )}

        {audienceType === 'specific_students' && (
          <>
            <Autocomplete
              options={sessions}
              getOptionLabel={(o) => o.name || `Session ${o.id}`}
              value={sessions.find((s) => String(s.id) === studentSearchSession) || null}
              onChange={(_, v) => {
                setStudentSearchSession(v ? String(v.id) : '');
                setSelectedStudents([]);
              }}
              disabled={readOnly}
              renderInput={(params) => <TextField {...params} label="Session for student list" margin="normal" />}
            />
            <Autocomplete
              multiple
              options={studentOptions}
              getOptionLabel={(o) => `${o.first_name || ''} ${o.last_name || ''}`.trim() || `#${o.id}`}
              value={selectedStudents}
              onChange={(_, v) => setSelectedStudents(v)}
              disabled={readOnly || !studentSearchSession}
              renderInput={(params) => <TextField {...params} label="Students" margin="normal" />}
            />
          </>
        )}

        <Collapse in={Boolean(previewDetail)}>
          <Paper variant="outlined" sx={{ p: 2, mt: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
              Preview
            </Typography>
            {previewDetail && (
              <>
                {previewDetail.audience_kind === 'staff' ? (
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    {previewDetail.staff_audience_type === 'staff_by_position' &&
                    (previewDetail.staff_group_label || '').trim() ? (
                      <>
                        Will reach <strong>{previewDetail.recipient_count}</strong>{' '}
                        <strong>{previewDetail.staff_group_label}</strong> staff members (
                        <strong>{previewDetail.skipped_count}</strong> skipped — no phone)
                      </>
                    ) : (
                      <>
                        Will reach <strong>{previewDetail.recipient_count}</strong> staff members (
                        <strong>{previewDetail.skipped_count}</strong> skipped — no phone)
                      </>
                    )}
                  </Typography>
                ) : (
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    Will reach <strong>{previewDetail.recipient_count}</strong> recipients across{' '}
                    <strong>{previewDetail.student_count ?? '—'}</strong> students (
                    <strong>{previewDetail.skipped_count}</strong> skipped)
                  </Typography>
                )}
                {previewDetail.audience_kind !== 'staff' && (
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                    Recipients = unique phone numbers; the total can exceed student count when both parents have
                    separate cells or when some rows are SMS and others are voice-only.
                  </Typography>
                )}
                {previewDetail.skipped_count > 0 && previewDetail.skip_breakdown && (
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                    {Object.entries(previewDetail.skip_breakdown)
                      .filter(([, n]) => n > 0)
                      .map(([k, n]) => `${n} ${k.replace(/_/g, ' ')}`)
                      .join(', ')}
                  </Typography>
                )}
                {Array.isArray(previewDetail.sample_recipients) && previewDetail.sample_recipients.length > 0 && (
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
                    Sample: {previewDetail.sample_recipients.slice(0, 5).join(', ')}
                  </Typography>
                )}
                <Typography variant="caption" color="text.secondary">
                  SMS preview
                </Typography>
                <Box
                  sx={{
                    border: '2px solid #ccc',
                    borderRadius: 2,
                    p: 2,
                    maxWidth: 320,
                    bgcolor: '#f5f5f5',
                    fontFamily: 'system-ui',
                    fontSize: 14,
                    whiteSpace: 'pre-wrap',
                    mb: 2,
                  }}
                >
                  {bodyPlain || '—'}
                </Box>
                <Typography variant="caption" color="text.secondary">
                  Voice (this text will be read aloud)
                </Typography>
                <Paper variant="outlined" sx={{ p: 1.5, bgcolor: 'action.hover' }}>
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                    {bodyPlain || '—'}
                  </Typography>
                </Paper>
              </>
            )}
          </Paper>
        </Collapse>

        <Box sx={{ mt: 3, display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
          <Button variant="outlined" onClick={handlePreview} disabled={readOnly || previewing || !title.trim()}>
            {previewing ? 'Preview…' : 'Preview'}
          </Button>
          <Button variant="outlined" onClick={handleSaveDraft} disabled={readOnly || savingDraft || !title.trim()}>
            {savingDraft ? 'Saving…' : 'Save draft'}
          </Button>
          {!readOnly && (
            <Button
              variant="contained"
              disabled={!campaignId || sending}
              onClick={() => setConfirmSendOpen(true)}
            >
              Send
            </Button>
          )}
          <Button variant="text" onClick={() => navigate('/communications')}>
            Cancel
          </Button>
        </Box>
      </Container>

      <Dialog open={confirmSendOpen} onClose={() => setConfirmSendOpen(false)}>
        <DialogTitle>Send campaign?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">{sendConfirmText}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmSendOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSend} disabled={sending}>
            {sending ? 'Sending…' : 'Confirm send'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={Boolean(toast)} autoHideDuration={4000} onClose={() => setToast('')} message={toast} />
    </Box>
  );
}
